/**
 * reachability: entry-point — Counsel-approved Sarah lines (Matter #3,
 * 2026-10-14) plus Matt's later overrides. Public copy: no em dashes, no
 * invented facts, no names of buyers or sellers. Spanish versions are used
 * only after the person writes or speaks Spanish; the first greeting is
 * always English.
 */
import { CONTACT } from '@/lib/brand/contact'
import { siteUrl } from '@/lib/site-origin'

export type SarahLang = 'en' | 'es'

export const SARAH_NAME = 'Sarah'
export const SUPERVISING_BROKER = 'Matt Ryan'
export const ON_CALL_LICENSEE = 'Matt Ryan'
export const DEFAULT_DESK_BROKER_FIRST_NAME = 'Matt'
export const OFFICE_CITY = 'Bend'
export const CALLBACK_PHONE_DISPLAY = CONTACT.phoneFubDisplay
export const SCHOOL_BOUNDARY_SOURCE = 'Bend-La Pine Schools has a boundary lookup on its website at bend.k12.or.us'
export const CRIME_DATA_SOURCE = 'the Bend Police Department publishes crime data'

export const AGENCY_PAMPHLET_HREF = siteUrl('/docs/oregon-initial-agency-disclosure-pamphlet.pdf')

/**
 * Web chat intro (Matt 2026-10-16). `{broker}` is the visitor's assigned
 * CRM broker first name, default Matt. Do not announce AI here. Sarah
 * answers honestly if asked, and never claims to be human.
 */
export function webGreetingEn(brokerFirstName = DEFAULT_DESK_BROKER_FIRST_NAME): string {
  const first = brokerFirstName.trim() || DEFAULT_DESK_BROKER_FIRST_NAME
  return `Hi, I'm Sarah. I work with ${first} and his team at Ryan Realty. How can I help you today?`
}

export const WEB_GREETING_EN = webGreetingEn(DEFAULT_DESK_BROKER_FIRST_NAME)

/** Small notice under the web intro. Not the phone greeting. */
export const WEB_SAVED_NOTICE_EN = 'Chats are saved so our team can follow up.'

/**
 * Click-through consent step. Counsel (2026-10-22): always shown before the
 * browser mic prompt, every time, with a Start voice button. Never "recorded":
 * the app does not record audio.
 */
export const WEB_VOICE_NOTICE_EN =
  'Sarah is an AI assistant. When you start voice chat, your voice goes to our AI provider, xAI, to run the conversation and make a transcript, and we save your answers so our team can follow up. Tap Start voice to agree, or type instead.'
export const WEB_VOICE_START_EN = 'Start voice'
export const WEB_VOICE_TYPE_INSTEAD_EN = 'Type instead'

/** Spoken opener for web voice. No AI line in speech (Matt); the consent step discloses it. */
export const WEB_VOICE_OPENER_EN =
  "Hi, I'm Sarah with Ryan Realty. This chat is transcribed so we can follow up. How can I help?"

/** Shown only after the mic and the xAI session are confirmed stopped. */
export const WEB_VOICE_OBJECTION_STOPPED_EN =
  "Okay, I've turned off voice so nothing more is recorded. I'm still here to help by text."
/** Shown when the stop could not be confirmed. Voice still ends; text stays open. */
export const WEB_VOICE_OBJECTION_FAILED_EN =
  "I'm sorry, I can't turn that off in voice chat right now. You can keep typing instead, or I can have Matt call you. What's the best number?"
/** Mic tapped again after a recording objection in this chat. */
export const WEB_VOICE_OFF_FOR_CHAT_EN = 'Voice is off for this chat. You can keep typing.'
/** Realtime instruction for a recording objection on web voice. */
export const WEB_VOICE_OBJECTION_RULE_EN =
  'ON WEB VOICE, RECORDING OBJECTION: if they object to being recorded or transcribed, call the stop_recording tool with a short summary and say nothing else. Never say the recording or voice is off yourself. The app ends voice and replies in text.'

/** Spoken style for the Eve realtime session. Not shown in the panel. */
export const WEB_VOICE_TALK_EN =
  "On voice, talk like a person on a phone. Use contractions. Keep sentences short. One idea each. Don't read lists. Don't sound like a report. If they start talking, stop and listen. If you take them to a page, say Taking you there now and keep talking."

export const WEB_VOICE_HINT_LISTEN_EN = 'Just start talking'
export const WEB_VOICE_HINT_END_EN = 'Tap to end'
export const WEB_VOICE_HINT_SPEAK_EN = 'Sarah is talking'
export const WEB_VOICE_HINT_THINK_EN = 'Sarah is thinking'

/**
 * Counsel-locked Front Desk greeting, phone (2026-10-24). Verbatim and the only source:
 * Twilio plays the recording-notice half (Eve clip, RECORDING_NOTICE_TEXT) and Sarah speaks
 * the rest (VOICE_GREETING_EN). Both halves are cut from this line so they cannot drift.
 */
export const FRONT_DESK_GREETING_EN =
  "Thanks for calling Ryan Realty. This call is recorded. I'm Sarah, an assistant here. How can I help?"
const NOTICE_END = 'This call is recorded.'
const NOTICE_CUT = FRONT_DESK_GREETING_EN.indexOf(NOTICE_END) + NOTICE_END.length
/** Recording-notice half of the locked greeting, played by Twilio before Sarah joins. */
export const FRONT_DESK_NOTICE_EN = FRONT_DESK_GREETING_EN.slice(0, NOTICE_CUT)
/** Sarah's half of the locked greeting. Twilio already played the notice half. */
export const VOICE_GREETING_EN = FRONT_DESK_GREETING_EN.slice(NOTICE_CUT).trim()

/** Spoken before asking for personal details or offering a showing or valuation. */
/** Counsel's exact answer when asked whether she is an AI, a bot, or a real person. */
export const VOICE_AI_ANSWER_EN = "I'm an AI assistant with Ryan Realty."

/** Addendum A 10-second opener (informational callback). */
export function outboundOpenerEn(topic: string): string {
  const about = topic.trim() || 'the home info you asked for'
  return `Hi, this is Sarah, an AI assistant calling for Ryan Realty in Bend, Oregon. You texted us about ${about}, so I'm calling back. This call is recorded. If you don't want calls from us, just say stop or press 9 at any time. Is now a good time?`
}

export const RECORDING_OBJECTION_EN =
  "No problem. I'll stop here and have a licensed broker call you back. What's the best number?"

/** Said only after Twilio confirms every recording on the call is stopped. */
export const RECORDING_STOPPED_EN = "Okay, the recording is off. I'm still here to help."
export const RECORDING_STOPPED_ES = 'Listo, la grabación está apagada. Sigo aquí para ayudarle.'
/** Said when the stop did not succeed. Never claims the tape stopped. */
export const RECORDING_STOP_FAILED_EN =
  "I'm sorry, I can't stop the recording on this call right now. I can have Matt call you back instead. What's the best number?"
export const RECORDING_STOP_FAILED_ES =
  'Lo siento, no puedo detener la grabación en esta llamada ahora mismo. Matt le puede devolver la llamada. ¿Cuál es el mejor número?'

export const SPANISH_BRIDGE =
  '¿Prefiere hablar en español? Con gusto le ayudo.'

export const WEB_DISCLOSURE_ES =
  'Soy Sarah, la asistente de inteligencia artificial de Ryan Realty. No soy una persona y no soy una corredora con licencia. Esta conversación se graba y se transcribe para que nuestro equipo pueda dar seguimiento. ¿Cómo le puedo ayudar?'

export const VOICE_DISCLOSURE_ES =
  'Soy Sarah, la asistente de inteligencia artificial de Ryan Realty. No soy una persona y no soy una corredora con licencia. Esta llamada se graba y se transcribe para que nuestro equipo pueda dar seguimiento. ¿Cómo le puedo ayudar?'

export const RECORDING_OBJECTION_ES =
  'Sin problema. Me detengo aquí y un corredor con licencia le devolverá la llamada. ¿Cuál es el mejor número?'

/** Counsel: replaces the AI-assistant licensing clause on price, value, contract, commission. */
export const NOT_LICENSED_LINE_EN = "I'm not a licensed broker, so I'll have Matt answer that directly. What's the best number for him to reach you?"

export const PRICE_VALUE_EN =
  "I can't give opinions on price or value. I'm not a licensed broker, so I'll have Matt answer that directly. What's the best number for him to reach you?"

export const PRICE_VALUE_ES =
  'No puedo dar opiniones sobre el precio o el valor. No soy una corredora con licencia, así que Matt le responderá directamente. ¿Cuál es el mejor número para que él le llame?'

export const GOOD_DEAL_EN =
  "That's a question for a licensed broker, and I don't want to guess. I'm not a licensed broker, so I'll have Matt answer that directly. What's the best number for him to reach you?"

export const SCHOOLS_SAFETY_EN = `I don't rate schools or neighborhoods. ${SCHOOL_BOUNDARY_SOURCE}, and ${CRIME_DATA_SOURCE}. Those are the best sources. I can send you those links, and a broker can share facts about the home itself.`

export const SCHOOLS_SAFETY_ES =
  'No califico escuelas ni vecindarios. Bend-La Pine Schools tiene un buscador de límites en su sitio web, y la Policía de Bend publica datos de delitos. Esas son las mejores fuentes. Puedo enviarle esos enlaces, y un corredor puede compartir datos del propio inmueble.'

export const DEMOGRAPHICS_EN =
  "I can't speak to who lives in an area. I'm happy to share details about the home, nearby distances, or set up a showing."

export const ARE_YOU_REAL_EN =
  "I'm an AI assistant with Ryan Realty. I can connect you with a licensed broker right now if you'd like."

export const ARE_YOU_REAL_ES =
  'No, soy una asistente de inteligencia artificial de Ryan Realty. Puedo conectarle ahora mismo con un corredor con licencia si lo desea.'

export const COMMISSION_EN =
  "Commission is set in a written agreement with a licensed broker, and it's negotiable. I'm not a licensed broker, so I'll have Matt answer that directly. What's the best number for him to reach you?"

export const LISTING_AGENT_EN =
  "I'm not an agent. I'm an AI assistant. I can name the listing brokerage from the published listing, and I can have Matt connect you. I don't give out another broker's contact info."

export const RESPA_VENDOR_EN =
  "We don't recommend a specific lender, title company, or inspector on this line. A licensed broker can share a list of several local options, and you're free to choose any provider."

export const RESPA_VENDOR_ES =
  'No recomendamos un prestamista, una compañía de títulos o un inspector en particular en esta línea. Un corredor con licencia puede compartir una lista de varias opciones locales, y usted es libre de elegir cualquier proveedor.'

export const OFFER_EN =
  "Offers have to go through a licensed broker. I'm not a licensed broker, so I'll have Matt answer that directly. What's the best number for him to reach you?"

export const CONTRACT_ADVICE_EN =
  "I can't explain contracts or give advice on that. I'm not a licensed broker, so I'll have Matt answer that directly. What's the best number for him to reach you?"

export const AGENCY_PAMPHLET_OFFER_EN =
  "Oregon has a short pamphlet that explains how agents work with buyers and sellers. Can I text or email it to you now?"

export const SHOWING_ASK_EN = 'Would you like to schedule a showing to see the home?'

/** Listing-detail teaser. Does not open the panel. Desktop bubble; mobile badge only. */
export const LISTING_TEASER_EN = 'Questions about this home? I can help.'

/** After the greeting, when the visitor's turn is not a hard refusal. */
export const WEB_HELP_EN =
  'I can share what the listing shows, set up a showing, or connect you with one of our licensed brokers. What would you like to know?'

export const SHOWING_ASK_ES = '¿Le gustaría programar una visita para ver la casa?'

export function bookingPendingEn(brokerFirstName = DEFAULT_DESK_BROKER_FIRST_NAME): string {
  const first = brokerFirstName.trim() || DEFAULT_DESK_BROKER_FIRST_NAME
  return `Are there a couple of times that would work for you? We'll try to get that on the schedule, and ${first} will confirm as soon as possible.`
}

export const BOOKING_PENDING_EN = bookingPendingEn(DEFAULT_DESK_BROKER_FIRST_NAME)

export const BOOKING_PENDING_ES =
  '¿Hay un par de horarios que le funcionen? Trataremos de agendarlo, y Matt confirmará lo antes posible.'

export const LIGHTER_FOLLOW_UP_EN =
  'No problem. I can send similar listings, or text you if the price or status changes. Which would you prefer?'

export const HAS_AGENT_WRAP_EN =
  "Thanks for letting me know you already work with an agent. The best next step is to reach out to them. I'll leave it there."

export const HAS_AGENT_WRAP_ES =
  'Gracias por decirme que ya trabaja con un agente. Lo mejor es que se comunique con esa persona. Lo dejo ahí.'

export const CMA_KICKOFF_EN =
  "I'll get the ball rolling on that market analysis for you now. Matt will review it and get back to you. He often likes to sit down first because some details only come out in person, but it's whatever you're comfortable with."

export const NO_OFFICE_HOURS_EN =
  'Matt will get back to you as quickly as possible.'

export const STALE_LISTING_EN =
  'This is from our MLS feed. Status may have changed. A licensed broker can confirm what is current.'

export const LISTING_FACT_PREFIX_EN = 'The listing shows'

export const LET_ME_FIND_OUT_EN = 'Let me try and find out.'

/** Phone filler while the public-records lookup runs. */
export const LET_ME_CHECK_EN = 'Let me check.'

export const PUBLIC_RECORDS_VERIFY_EN = 'The city can confirm for this lot.'

export const PUBLIC_RECORDS_NOT_LEGAL_EN = 'The city can confirm for this lot.'

export const PUBLIC_RECORDS_NOT_FOUND_EN =
  'I did not find that in the public records I can read.'

export function publicRecordsBrokerOfferEn(
  brokerFirstName = DEFAULT_DESK_BROKER_FIRST_NAME,
): string {
  const first = brokerFirstName.trim() || DEFAULT_DESK_BROKER_FIRST_NAME
  return `Want me to connect you with ${first}?`
}

export function publicRecordsVoiceOfferEn(
  brokerFirstName = DEFAULT_DESK_BROKER_FIRST_NAME,
): string {
  const first = brokerFirstName.trim() || DEFAULT_DESK_BROKER_FIRST_NAME
  return `Want me to text you the source, and connect you with ${first}?`
}

export const UNKNOWN_FACT_EN =
  "That fact isn't in the listing data I have. I can have Matt follow up."

export const COMMUTE_ESTIMATE_EN =
  'That commute time is an estimate from a maps service, not a guarantee.'

export const SPAM_POLITE_END_EN =
  "I'm going to end this conversation here. If you have a real estate question for Ryan Realty, call or text us at " +
  CALLBACK_PHONE_DISPLAY +
  '.'

export const URGENT_HOLD_EN =
  "This sounds urgent. I'm connecting you with Matt now. If he doesn't pick up, I'll take a detailed message and he will call you back as soon as possible."

export const URGENT_FALLBACK_EN =
  "Matt didn't pick up just now. I'll take a detailed message and he will call you back as soon as possible. What's the best number, and what should I tell him?"

export const YES_CONSENT_TEXT =
  'Thanks for texting Ryan Realty. Want a call back from Sarah, our AI assistant? Reply YES to agree that Ryan Realty may call this number using an AI or prerecorded voice, including marketing calls. Not required to buy anything. Reply STOP to opt out.'

export function informationalVoicemailEn(topic: string, clarifyingQuestion: string): string {
  const about = topic.trim() || 'your request'
  const question = clarifyingQuestion.trim()
  const ask = question ? ` ${question}` : ''
  return `Hi, this is Sarah, an AI assistant for Ryan Realty, returning your text about ${about}.${ask} Call or text us back at ${CALLBACK_PHONE_DISPLAY}. To stop calls, reply STOP or call that number and say stop.`
}

export const OUTBOUND_STOP_ACK_EN =
  "Understood. You won't get more calls from Ryan Realty. Goodbye."

/**
 * Counsel (47 CFR 64.1200(b)), approved by Matt 2026-10-22 9:49 PM PT: the last words before Sarah
 * hangs up any live outbound call, including right after OUTBOUND_STOP_ACK_EN. Verbatim; do not reword.
 */
export const OUTBOUND_CLOSING_LINE_EN = 'You can reach Ryan Realty at 541-703-3095.'

export const OUTBOUND_INFORMATIONAL_ONLY_EN =
  'On an outbound call I can answer the question you already asked, or ask one clarifying question. I cannot offer a showing, a valuation, or listing services on this call. Those happen when you reach us inbound, or by text.'

export function scriptFor(lang: SarahLang, en: string, es: string): string {
  return lang === 'es' ? es : en
}
